import React from "react";
import { StyleSheet, View } from "react-native";
import { CheckCircle2, Trash2 } from "lucide-react";
import { MotiView } from "moti";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import {
  getSavedConfigurationSummary,
  getSavedConfigurationActionLabel,
  getConnectorConnectionPattern,
  type CatalogConnector,
  type ConnectorApprovalSummary,
  type InstitutionalConnectionSummary,
  usesConnectorApprovalFlow,
  usesSavedConfigurationRedirect,
} from "../catalog.utils";

type PendingAction = "approve" | "activate" | "disconnect" | "revoke" | "test" | null;

interface ConnectorDetailsFooterProps {
  connector: CatalogConnector;
  connection: InstitutionalConnectionSummary | null;
  approval: ConnectorApprovalSummary | null;
  pendingAction: PendingAction;
  isActivated: boolean;
  canActivate?: boolean;
  requireTestBeforeActivation?: boolean;
  onClose: () => void;
  onApproveApp: () => void;
  onTestConnection: () => void;
  onActivate: () => void;
  onDisconnect: () => void;
  onRevokeApproval: () => void;
}

export function ConnectorDetailsFooter({
  connector,
  connection,
  approval,
  pendingAction,
  isActivated,
  canActivate = true,
  requireTestBeforeActivation = false,
  onClose,
  onApproveApp,
  onTestConnection,
  onActivate,
  onDisconnect,
  onRevokeApproval,
}: ConnectorDetailsFooterProps) {
  const showsApproval = usesConnectorApprovalFlow(connector);
  const isConnected = connection !== null;
  const connectionPattern = getConnectorConnectionPattern(connector);
  const savedConfiguration = getSavedConfigurationSummary(connector);
  const savedConfigurationActionLabel = getSavedConfigurationActionLabel(connector);
  const savedConfigurationPendingText = usesSavedConfigurationRedirect(connector)
    ? "Opening OAuth..."
    : "Testing connection...";

  if (connectionPattern === "personal-only") {
    return (
      <View style={styles.footer}>
        <CustomButton
          text="Close"
          size="m"
          onPress={onClose}
          useTranslationText={false}
          styleType="primaryLight"
          style={styles.fullWidthAction}
          textStyle={styles.cancelText}
        />
      </View>
    );
  }

  return (
    <View style={styles.footer}>
      {savedConfiguration && !isConnected && !approval && requireTestBeforeActivation && (
        <View style={styles.footerActionsTertiary}>
          <CustomButton
            text="Delete configuration"
            size="m"
            leftIcon={<Trash2 size={14} color="#B91C1C" />}
            onPress={onDisconnect}
            loading={pendingAction === "disconnect"}
            useTranslationText={false}
            style={styles.tertiaryActionButton}
            textStyle={styles.destructiveText}
          />
          <CustomButton
            text={pendingAction === "test" ? savedConfigurationPendingText : savedConfigurationActionLabel}
            size="m"
            onPress={onTestConnection}
            loading={pendingAction === "test"}
            useTranslationText={false}
            styleType="primaryLight"
            style={styles.tertiaryActionButton}
            textStyle={styles.cancelText}
          />
          <CustomButton
            text={pendingAction === "activate" ? "Activating..." : "Activate"}
            size="m"
            onPress={onActivate}
            loading={pendingAction === "activate"}
            disabled={!canActivate}
            useTranslationText={false}
            styleType="primary"
            style={styles.tertiaryActionButton}
            textStyle={styles.primaryText}
          />
        </View>
      )}

      {savedConfiguration && !isConnected && !approval && !requireTestBeforeActivation && (
        <View style={styles.footerActions}>
          <CustomButton
            text="Delete configuration"
            size="m"
            leftIcon={<Trash2 size={14} color="#B91C1C" />}
            onPress={onDisconnect}
            loading={pendingAction === "disconnect"}
            useTranslationText={false}
            style={styles.secondarySplitButton}
            textStyle={styles.destructiveText}
          />
          <CustomButton
            text={pendingAction === "activate" ? savedConfigurationPendingText : savedConfigurationActionLabel}
            size="m"
            onPress={onActivate}
            loading={pendingAction === "activate"}
            useTranslationText={false}
            styleType="primary"
            style={styles.primarySplitButton}
            textStyle={styles.primaryText}
          />
        </View>
      )}

      {savedConfiguration && approval && (
        <View style={styles.footerActions}>
          <CustomButton
            text="Revoke Approval"
            size="s"
            leftIcon={<Trash2 size={14} color="#B91C1C" />}
            onPress={onRevokeApproval}
            loading={pendingAction === "revoke"}
            useTranslationText={false}
            style={styles.secondarySplitButton}
            textStyle={styles.destructiveText}
          />
          <CustomButton
            text={pendingAction === "activate" ? savedConfigurationPendingText : savedConfigurationActionLabel}
            size="m"
            onPress={onActivate}
            loading={pendingAction === "activate"}
            useTranslationText={false}
            styleType="primary"
            style={styles.primarySplitButton}
            textStyle={styles.primaryText}
          />
        </View>
      )}

      {!savedConfiguration &&
        showsApproval &&
        !approval &&
        (isActivated ? (
          <MotiView
            from={{ opacity: 0, translateY: 6 }}
            animate={{ opacity: 1, translateY: 0 }}
            transition={{ type: "timing", duration: 200 }}>
            <View style={styles.successRow}>
              <CheckCircle2 size={16} color="#3B7C0F" />
              <CustomText
                text={`${connector.name} approved`}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="medium"
                style={styles.successText}
              />
            </View>
          </MotiView>
        ) : (
          <View style={styles.footerActions}>
            <CustomButton
              text="Cancel"
              size="m"
              onPress={onClose}
              useTranslationText={false}
              styleType="primaryLight"
              style={styles.secondarySplitButton}
              textStyle={styles.cancelText}
            />
            <CustomButton
              text={pendingAction === "approve" ? "Approving..." : "Approve App"}
              size="m"
              onPress={onApproveApp}
              loading={pendingAction === "approve"}
              useTranslationText={false}
              styleType="primary"
              style={styles.primarySplitButton}
              textStyle={styles.primaryText}
            />
          </View>
        ))}

      {!showsApproval && isConnected && (
        <CustomButton
          text={`Disconnect ${connector.name}`}
          size="s"
          leftIcon={<Trash2 size={14} color="#B91C1C" />}
          onPress={onDisconnect}
          loading={pendingAction === "disconnect"}
          useTranslationText={false}
          style={styles.fullWidthAction}
          textStyle={styles.destructiveText}
        />
      )}

      {!savedConfiguration && !showsApproval && !isConnected && requireTestBeforeActivation && !isActivated && (
        <View style={styles.footerActions}>
          <CustomButton
            text={pendingAction === "test" ? "Testing connection..." : "Test Connection"}
            size="m"
            onPress={onTestConnection}
            loading={pendingAction === "test"}
            useTranslationText={false}
            styleType="primaryLight"
            style={styles.secondarySplitButton}
            textStyle={styles.cancelText}
          />
          <CustomButton
            text={pendingAction === "activate" ? "Activating..." : "Activate"}
            size="m"
            onPress={onActivate}
            loading={pendingAction === "activate"}
            disabled={!canActivate}
            useTranslationText={false}
            styleType="primary"
            style={styles.primarySplitButton}
            textStyle={styles.primaryText}
          />
        </View>
      )}

      {!savedConfiguration &&
        !showsApproval &&
        !isConnected &&
        !requireTestBeforeActivation &&
        (isActivated ? (
          <MotiView
            from={{ opacity: 0, translateY: 6 }}
            animate={{ opacity: 1, translateY: 0 }}
            transition={{ type: "timing", duration: 200 }}>
            <View style={styles.successRow}>
              <CheckCircle2 size={16} color="#3B7C0F" />
              <CustomText
                text={`${connector.name} activated`}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="medium"
                style={styles.successText}
              />
            </View>
          </MotiView>
        ) : (
          <View style={styles.footerActions}>
            <CustomButton
              text="Close"
              size="m"
              onPress={onClose}
              useTranslationText={false}
              styleType="primaryLight"
              style={styles.secondarySplitButton}
              textStyle={styles.cancelText}
            />
            <CustomButton
              text={pendingAction === "activate" ? "Saving..." : "Save configuration"}
              size="m"
              onPress={onActivate}
              loading={pendingAction === "activate"}
              useTranslationText={false}
              styleType="primary"
              style={styles.primarySplitButton}
              textStyle={styles.primaryText}
            />
          </View>
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    gap: 8,
  },
  footerActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerActionsTertiary: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  primarySplitButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    height: 34,
    width: "49%",
  },
  secondarySplitButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    height: 34,
    width: "49%",
    borderWidth: 1,
    borderColor: "#0000001A",
    justifyContent: "center",
    alignItems: "center",
  },
  tertiaryActionButton: {
    flex: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    height: 34,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#0000001A",
  },
  fullWidthAction: {
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    borderWidth: 1,
    borderColor: "#0000001A",
  },
  destructiveText: {
    color: "#B91C1C",
    fontSize: 12,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "500",
  },
  cancelText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#344054",
  },
  successRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 4,
    justifyContent: "center",
  },
  successText: {
    color: "#3B7C0F",
  },
});
